import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, LoadingSkeleton } from './ui';
import { UserGroupIcon, UserIcon, BanknotesIcon, ClockIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';

const statConfig = [
  { key: 'total_students', label: 'Total Students', icon: UserGroupIcon, accent: 'primary', prefix: '', isCurrency: false },
  { key: 'total_teachers', label: 'Total Teachers', icon: UserIcon, accent: 'secondary', prefix: '', isCurrency: false },
  { key: 'total_revenue_usd', label: 'Revenue (USD)', icon: BanknotesIcon, accent: 'primary', prefix: '$', isCurrency: true },
  { key: 'outstanding_fees_usd', label: 'Outstanding Fees', icon: ClockIcon, accent: 'secondary', prefix: '$', isCurrency: true },
];

function StatCard({ label, value, icon: Icon, accent, prefix, isCurrency }) {
  const formatted = typeof value === 'number'
    ? value.toLocaleString(undefined, isCurrency
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { maximumFractionDigits: 0 })
    : value;

  const color = accent === 'secondary' ? 'var(--color-secondary)' : 'var(--color-primary)';
  // --color-primary-50 is a hand-tuned pale tint shipped per theme preset;
  // no secondary equivalent exists, so color-mix() derives an equally pale
  // tint from --color-secondary at render time.
  const background = accent === 'secondary'
    ? 'color-mix(in srgb, var(--color-secondary) 15%, var(--color-surface))'
    : 'var(--color-primary-50)';

  return (
    <div
      style={{
        background,
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        padding: '1.5rem',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>{label}</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1, color: 'var(--color-text)' }}>
            {prefix}{formatted}
          </div>
        </div>
        <Icon style={{ width: 32, height: 32, color }} />
      </div>
    </div>
  );
}

function DashboardSummary({ token }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/dashboard/summary', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setSummary(await response.json());
      } else {
        setError(await safeErrorMessage(response, 'Failed to load dashboard'));
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) return <LoadingSkeleton type="stat" rows={4} />;
  if (error) return <div className="alert alert-error">{error}</div>;

  const classColumns = [
    { header: 'Class', accessor: 'class' },
    { header: 'Students', accessor: 'count' },
  ];

  const paymentColumns = [
    { header: 'Receipt', accessor: 'receipt_number' },
    { header: 'Student', accessor: 'student_name' },
    {
      header: 'Amount',
      render: (row) => `${row.amount} ${row.currency}`,
    },
    {
      header: 'Date',
      render: (row) => new Date(row.payment_date).toLocaleDateString(),
    },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {statConfig.map((stat) => (
          <StatCard key={stat.key} {...stat} value={summary[stat.key] || 0} />
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
        <Card title="Students by Class">
          <Table columns={classColumns} data={summary.students_by_class || []} />
        </Card>

        <Card title="Recent Payments">
          <Table
            columns={paymentColumns}
            data={summary.recent_payments || []}
            emptyMessage="No recent payments"
          />
        </Card>
      </div>
    </div>
  );
}

export default DashboardSummary;
